import { Pencil, DollarSign, MapPin, Calendar, Clock, Shield } from 'lucide-react';
import React, { useState , useEffect } from "react";
import { useForm, Controller } from "react-hook-form";
import { yupResolver } from "@hookform/resolvers/yup";
import * as yup from "yup";
import Select from "react-select";







import { updateProviderResourcePreferences } from "../../../../../services/staffingService";

// Validation schema for Preferences
const preferencesSchema = yup.object().shape({
  minHourlyRate: yup.number().required("Min hourly rate is required"),
  maxDistanceInMiles: yup.number().required("Max distance is required"),
  shiftDays: yup
    .array()
    .of(yup.string())
    .min(1, "At least one shift day must be selected")
    .required(),
  shiftTimes: yup
    .array()
    .of(yup.string())
    .min(1, "At least one shift time must be selected")
    .required(),
  servicesOffered: yup
    .array()
    .of(yup.string())
    .min(1, "At least one service must be selected")
    .required(),
});

const Preferences = ({ officerData: initialOfficerData , providerCompanyId }) => {
  const [isEditing, setIsEditing] = useState(false);
  const [officerData, setOfficerData] = useState(initialOfficerData);

  const {
    register,
    handleSubmit,
    control,
    formState: { errors },
  } = useForm({
    resolver: yupResolver(preferencesSchema),
    defaultValues: {
      minHourlyRate: officerData.preferences.minHourlyRate,
      maxDistanceInMiles: officerData.preferences.maxDistanceInMiles,
      shiftDays: officerData.preferences.shiftDays,
      shiftTimes: officerData.preferences.shiftTimes,
      servicesOffered: officerData.servicesOffered,
    },
  });

  useEffect(() => {
    setOfficerData(initialOfficerData);
  }, [initialOfficerData]);

  const toggleEditMode = () => {
    setIsEditing(!isEditing);
  };


  const onSubmit = async (data) => {
    try {

      // Make the API call here to update preferences
      await updateProviderResourcePreferences(providerCompanyId, officerData._id, {
        minHourlyRate: data.minHourlyRate,
        maxDistanceInMiles: data.maxDistanceInMiles,
        shiftDays: data.shiftDays,
        shiftTimes: data.shiftTimes,
        servicesOffered: data.servicesOffered,
      });

      setOfficerData((prevData) => ({
        ...prevData,
        preferences: {
          ...prevData.preferences,
          minHourlyRate: data.minHourlyRate,
          maxDistanceInMiles: data.maxDistanceInMiles,
          shiftDays: data.shiftDays,
          shiftTimes: data.shiftTimes,
        },
        servicesOffered: data.servicesOffered,
      }));

      setIsEditing(false);
    } catch (error) {
      console.error('Failed to update preferences:', error);
    }
  };

  const shiftDayOptions = [
    { value: "Monday", label: "Monday" },
    { value: "Tuesday", label: "Tuesday" },
    { value: "Wednesday", label: "Wednesday" },
    { value: "Thursday", label: "Thursday" },
    { value: "Friday", label: "Friday" },
    { value: "Saturday", label: "Saturday" },
    { value: "Sunday", label: "Sunday" },
  ];

  const shiftTimeOptions = [
    { value: "Morning", label: "Morning" },
    { value: "Afternoon", label: "Afternoon" },
    { value: "Evening", label: "Evening" },
    { value: "Night", label: "Night" },
  ];

  const serviceOfferedOptions = [
    { value: "standing_post", label: "Standing Post" },
    { value: "mobile_patrol", label: "Mobile Patrol" },
    { value: "loss_prevention", label: "Loss Prevention" },
    { value: "event_security", label: "Event Security" },
  ];

  return (
    <div className="font-inter">
      {!isEditing ? (
        <div className="space-y-4">
          <div className="space-y-0.5">
            <label className="text-xs font-medium text-[var(--muted-foreground)] uppercase tracking-wide">Preferred Hourly Rate</label>
            <div className="flex items-center gap-2">
              <DollarSign className="text-[var(--brand-pink)]" style={{ fontSize: '14px' }} />
              <p className="text-[var(--foreground)] font-semibold text-sm">
                {officerData.preferences?.minHourlyRate ? `$${officerData.preferences.minHourlyRate} and up` : 'Not specified'}
              </p>
            </div>
          </div>

          <div className="space-y-0.5">
            <label className="text-xs font-medium text-[var(--muted-foreground)] uppercase tracking-wide">Max Travel Distance</label>
            <div className="flex items-center gap-2">
              <MapPin className="text-[var(--brand-pink)]" style={{ fontSize: '14px' }} />
              <p className="text-[var(--foreground)] font-medium text-sm">
                {officerData.preferences?.maxDistanceInMiles ? `${officerData.preferences.maxDistanceInMiles} miles` : 'Not specified'}
              </p>
            </div>
          </div>

          <div className="space-y-0.5">
            <label className="text-xs font-medium text-[var(--muted-foreground)] uppercase tracking-wide">Preferred Shift Days</label>
            <div className="flex items-start gap-2">
              <Calendar className="text-[var(--brand-pink)]" style={{ fontSize: '14px', marginTop: '2px' }} />
              <div className="flex flex-wrap gap-2">
                {officerData.preferences?.shiftDays?.length > 0 ? (
                  officerData.preferences.shiftDays.map((day) => (
                    <span 
                      key={day}
                      className="inline-flex items-center px-3 py-1 rounded-full text-xs font-medium bg-blue-100 text-blue-800"
                    >
                      {day}
                    </span>
                  ))
                ) : (
                  <p className="text-[var(--foreground)] font-medium text-sm">Not specified</p>
                )}
              </div>
            </div>
          </div>

          <div className="space-y-0.5">
            <label className="text-xs font-medium text-[var(--muted-foreground)] uppercase tracking-wide">Preferred Shift Times</label>
            <div className="flex items-start gap-2">
              <Clock className="text-[var(--brand-pink)]" style={{ fontSize: '14px', marginTop: '2px' }} />
              <div className="flex flex-wrap gap-2">
                {officerData.preferences?.shiftTimes?.length > 0 ? (
                  officerData.preferences.shiftTimes.map((time) => (
                    <span 
                      key={time}
                      className="inline-flex items-center px-3 py-1 rounded-full text-xs font-medium bg-green-100 text-green-800"
                    >
                      {time}
                    </span>
                  ))
                ) : (
                  <p className="text-[var(--foreground)] font-medium text-sm">Not specified</p>
                )}
              </div>
            </div>
          </div>

          <div className="space-y-0.5">
            <label className="text-xs font-medium text-[var(--muted-foreground)] uppercase tracking-wide">Services Offered</label>
            <div className="flex items-start gap-2">
              <Shield className="text-[var(--brand-pink)]" style={{ fontSize: '14px', marginTop: '2px' }} />
              <div className="flex flex-wrap gap-2">
                {officerData.servicesOffered?.length > 0 ? (
                  officerData.servicesOffered.map((service) => {
                    const readableService = service
                      .replace("_", " ")
                      .replace(/\b\w/g, (char) => char.toUpperCase());
                    return (
                      <span 
                        key={service}
                        className="inline-flex items-center px-3 py-1 rounded-full text-xs font-medium bg-purple-100 text-purple-800"
                      >
                        {readableService}
                      </span>
                    );
                  })
                ) : (
                  <p className="text-[var(--foreground)] font-medium text-sm">Not specified</p>
                )}
              </div>
            </div>
          </div>

          <div className="flex justify-end pt-6 border-t border-[var(--border)]">
            <button
              onClick={toggleEditMode}
              className="inline-flex items-center gap-2 px-5 py-2.5 text-[var(--primary)] bg-transparent border border-[var(--primary)] rounded-lg hover:bg-[var(--primary)] hover:text-white transition-all duration-200 font-medium text-sm"
            >
              <Pencil style={{ fontSize: '16px' }} />
              Edit Preferences
            </button>
          </div>
        </div>
      ) : (
        <form onSubmit={handleSubmit(onSubmit)} className="space-y-6">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Min Hourly Rate
            </label>
            <input
              type="number"
              {...register("minHourlyRate")}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
            />
            {errors.minHourlyRate && (
              <p className="mt-1 text-sm text-red-600">
                {errors.minHourlyRate.message}
              </p>
            )}
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Max Distance (in miles)
            </label>
            <input
              type="number"
              {...register("maxDistanceInMiles")}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
            />
            {errors.maxDistanceInMiles && (
              <p className="mt-1 text-sm text-red-600">
                {errors.maxDistanceInMiles.message}
              </p>
            )}
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Shift Days
            </label>
            <Controller
              name="shiftDays"
              control={control}
              render={({ field }) => (
                <Select
                  isMulti
                  options={shiftDayOptions}
                  value={field.value.map((day) => ({
                    value: day,
                    label: day,
                  }))}
                  onChange={(selected) =>
                    field.onChange(selected.map((option) => option.value))
                  }
                  className="react-select-container"
                  classNamePrefix="react-select"
                />
              )}
            />
            {errors.shiftDays && (
              <p className="mt-1 text-sm text-red-600">{errors.shiftDays.message}</p>
            )}
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Shift Times
            </label>
            <Controller
              name="shiftTimes"
              control={control}
              render={({ field }) => (
                <Select
                  isMulti
                  options={shiftTimeOptions}
                  value={field.value.map((time) => ({
                    value: time,
                    label: time,
                  }))}
                  onChange={(selected) =>
                    field.onChange(selected.map((option) => option.value))
                  }
                  className="react-select-container"
                  classNamePrefix="react-select"
                />
              )}
            />
            {errors.shiftTimes && (
              <p className="mt-1 text-sm text-red-600">{errors.shiftTimes.message}</p>
            )}
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Services Offered
            </label>
            <Controller
              name="servicesOffered"
              control={control}
              render={({ field }) => (
                <Select
                  isMulti
                  options={serviceOfferedOptions}
                  value={field.value.map((service) => ({
                    value: service,
                    label: service.replace(/_/g, " "),
                  }))}
                  onChange={(selected) =>
                    field.onChange(selected.map((option) => option.value))
                  }
                  className="react-select-container"
                  classNamePrefix="react-select"
                />
              )}
            />
            {errors.servicesOffered && (
              <p className="mt-1 text-sm text-red-600">
                {errors.servicesOffered.message}
              </p>
            )}
          </div>

          <div className="flex space-x-3">
            <button 
              type="submit" 
              className="px-4 py-2 bg-[#404880] text-white rounded-md hover:bg-[#2d3461] transition-colors duration-200"
            >
              Save
            </button>
            <button
              type="button"
              className="px-4 py-2 border border-gray-300 text-gray-700 rounded-md hover:bg-gray-50 transition-colors duration-200"
              onClick={toggleEditMode}
            >
              Cancel
            </button>
          </div>
        </form>
      )}
    </div>
  );
};

export default Preferences;
